import { useMemo, useState, useCallback } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  LabelList,
} from 'recharts';
import { clsx } from 'clsx';

// Chart title helper - prepends ticker in muted color when available
const ChartTitle = ({ ticker, title }: { ticker?: string; title: string }) => (
  <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
    {ticker && <span className="text-[var(--color-text-muted)]">{ticker}</span>}{ticker && ' — '}{title}
  </h3>
);

// Format large numbers (billions, millions)
const formatLargeNumber = (value: number): string => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (absValue >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (absValue >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

const formatPercent = (value: number): string => {
  if (value === null || value === undefined) return '-';
  return `${(value * 100).toFixed(1)}%`;
};

const formatPercentChange = (value: number): string => {
  if (value === null || value === undefined) return '';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(0)}%`;
};

const formatEPS = (value: number): string => {
  if (value === null || value === undefined) return '-';
  return `$${value.toFixed(2)}`;
};

// Format date for quarterly data: "Q1 '24", "Q2 '24", etc.
const formatQuarter = (dateStr: string): string => {
  const date = new Date(dateStr);
  const month = date.getMonth();
  const year = date.getFullYear().toString().slice(-2);

  // Determine quarter based on month
  let quarter: number;
  if (month <= 2) quarter = 1;       // Jan-Mar = Q1
  else if (month <= 5) quarter = 2;  // Apr-Jun = Q2
  else if (month <= 8) quarter = 3;  // Jul-Sep = Q3
  else quarter = 4;                   // Oct-Dec = Q4

  return `Q${quarter} '${year}`;
};

// Format date for annual data: just the year "2024"
const formatYear = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.getFullYear().toString();
};

// Calculate growth percentage
const calculateGrowth = (current: number | undefined, previous: number | undefined): number | undefined => {
  if (current === undefined || previous === undefined || previous === 0) return undefined;
  return ((current - previous) / Math.abs(previous)) * 100;
};

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
  dataKey: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
  formatFn?: (value: number, key: string) => string;
}

const CustomTooltip = ({ active, payload, label, formatFn }: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg">
      <p className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
        {label}
      </p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-[var(--color-text-muted)]">{entry.name}:</span>
            <span className="font-medium text-[var(--color-text-primary)]">
              {formatFn ? formatFn(entry.value, entry.dataKey) : entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Custom label for growth percentage on bars
interface GrowthLabelProps {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
}

const GrowthLabel = ({ x = 0, y = 0, width = 0, value }: GrowthLabelProps) => {
  if (value === undefined || value === null) return null;
  const color = value >= 0 ? '#10b981' : '#ef4444';
  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill={color}
      textAnchor="middle"
      fontSize={10}
      fontWeight="bold"
    >
      {formatPercentChange(value)}
    </text>
  );
};

// ============================================================
// EARNINGS CHART (Quarterly EPS vs Estimates)
// ============================================================

interface EarningsDataPoint {
  fiscal_date: string;
  reported_eps?: number;
  estimated_eps?: number;
  surprise?: number;
  surprise_percentage?: number;
}

interface EarningsChartProps {
  data: EarningsDataPoint[];
  className?: string;
  title?: string;
  ticker?: string;
}

export function EarningsChart({ data, className, title = 'Earnings History', ticker }: EarningsChartProps) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data]
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime())
      .slice(-8); // Last 8 quarters for better fit

    return sorted.map((point, index) => {
      const prevEps = index > 0 ? sorted[index - 1].reported_eps : undefined;
      const epsGrowth = calculateGrowth(point.reported_eps, prevEps);

      // Determine beat/miss based on reported vs estimated
      // If both values exist, compare them. Otherwise, just show as neutral (beat=null)
      let beat: boolean | null = null;
      if (point.reported_eps !== undefined && point.estimated_eps !== undefined) {
        beat = point.reported_eps > point.estimated_eps;
      }

      return {
        ...point,
        date: formatQuarter(point.fiscal_date),
        beat,
        epsGrowth,
      };
    });
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No earnings data available
      </div>
    );
  }

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={title} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={formatEPS}
            />
            <Tooltip
              content={
                <CustomTooltip
                  formatFn={(val, key) => {
                    if (key === 'epsGrowth') return formatPercentChange(val);
                    return formatEPS(val);
                  }}
                />
              }
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine y={0} stroke="var(--color-border)" strokeDasharray="3 3" />

            <Bar
              dataKey="reported_eps"
              name="Reported EPS"
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.beat === true ? '#10b981' : entry.beat === false ? '#ef4444' : '#3b82f6'}
                />
              ))}
              <LabelList dataKey="epsGrowth" content={<GrowthLabel />} />
            </Bar>
            <Line
              type="monotone"
              dataKey="estimated_eps"
              name="Estimated EPS"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#f59e0b', r: 4 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-[var(--color-text-muted)]">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <span>Beat</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-500" />
          <span>Missed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500" />
          <span>No Estimate</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// REVENUE & PROFIT CHART
// ============================================================

interface RevenueDataPoint {
  fiscal_date: string;
  total_revenue?: number;
  gross_profit?: number;
  operating_income?: number;
  net_income?: number;
}

interface RevenueChartProps {
  data: RevenueDataPoint[];
  className?: string;
  title?: string;
  ticker?: string;
  showNetIncome?: boolean;
}

export function RevenueChart({
  data,
  className,
  title = 'Revenue & Profitability',
  ticker,
  showNetIncome = true
}: RevenueChartProps) {
  // Determine if data is annual (gaps > 200 days between points)
  const isAnnual = useMemo(() => {
    if (!data || data.length < 2) return false;
    const sorted = [...data].sort((a, b) =>
      new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime()
    );
    const gap = new Date(sorted[1].fiscal_date).getTime() - new Date(sorted[0].fiscal_date).getTime();
    return gap > 200 * 24 * 60 * 60 * 1000; // > 200 days = annual
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data]
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime())
      .slice(-8); // Last 8 periods (quarters or years)

    return sorted.map((point, index) => {
      const prevRevenue = index > 0 ? sorted[index - 1].total_revenue : undefined;
      const revenueGrowth = calculateGrowth(point.total_revenue, prevRevenue);

      return {
        ...point,
        date: isAnnual ? formatYear(point.fiscal_date) : formatQuarter(point.fiscal_date),
        revenueGrowth,
      };
    });
  }, [data, isAnnual]);

  if (!data || data.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No revenue data available
      </div>
    );
  }

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={title} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={formatLargeNumber}
            />
            <Tooltip
              content={
                <CustomTooltip
                  formatFn={(val, key) => {
                    if (key === 'revenueGrowth') return formatPercentChange(val);
                    return formatLargeNumber(val);
                  }}
                />
              }
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine y={0} stroke="var(--color-border)" />

            <Bar
              dataKey="total_revenue"
              name="Revenue"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            >
              <LabelList dataKey="revenueGrowth" content={<GrowthLabel />} />
            </Bar>
            <Bar
              dataKey="gross_profit"
              name="Gross Profit"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
            />
            {showNetIncome && (
              <Bar
                dataKey="net_income"
                name="Net Income"
                fill="#a855f7"
                radius={[4, 4, 0, 0]}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-[var(--color-text-muted)]">
        <span>Growth % shown on revenue bars ({isAnnual ? 'YoY' : 'QoQ'})</span>
      </div>
    </div>
  );
}

// ============================================================
// CASH FLOW CHART
// ============================================================

interface CashFlowDataPoint {
  fiscal_date: string;
  operating_cashflow?: number;
  capital_expenditures?: number;
  free_cash_flow?: number;
}

interface CashFlowChartProps {
  data: CashFlowDataPoint[];
  className?: string;
  title?: string;
  ticker?: string;
}

export function CashFlowChart({ data, className, title = 'Cash Flow', ticker }: CashFlowChartProps) {
  // Determine if data is annual (gaps > 200 days between points)
  const isAnnual = useMemo(() => {
    if (!data || data.length < 2) return false;
    const sorted = [...data].sort((a, b) =>
      new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime()
    );
    const gap = new Date(sorted[1].fiscal_date).getTime() - new Date(sorted[0].fiscal_date).getTime();
    return gap > 200 * 24 * 60 * 60 * 1000; // > 200 days = annual
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data]
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime())
      .slice(-8);

    return sorted.map((point, index) => {
      const prevFCF = index > 0 ? sorted[index - 1].free_cash_flow : undefined;
      const fcfGrowth = calculateGrowth(point.free_cash_flow, prevFCF);

      return {
        ...point,
        date: isAnnual ? formatYear(point.fiscal_date) : formatQuarter(point.fiscal_date),
        capex: point.capital_expenditures ? -Math.abs(point.capital_expenditures) : undefined,
        fcfGrowth,
      };
    });
  }, [data, isAnnual]);

  if (!data || data.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No cash flow data available
      </div>
    );
  }

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={title} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={formatLargeNumber}
            />
            <Tooltip
              content={
                <CustomTooltip
                  formatFn={(val, key) => {
                    if (key === 'fcfGrowth') return formatPercentChange(val);
                    return formatLargeNumber(val);
                  }}
                />
              }
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine y={0} stroke="var(--color-border)" />

            <Bar
              dataKey="operating_cashflow"
              name="Operating Cash Flow"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="capex"
              name="CapEx"
              fill="#ef4444"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="free_cash_flow"
              name="Free Cash Flow"
              fill="#3b82f6"
              radius={[4, 4, 0, 0]}
            >
              <LabelList dataKey="fcfGrowth" content={<GrowthLabel />} />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-[var(--color-text-muted)]">
        <span>FCF = Operating Cash Flow - CapEx | Growth % shown ({isAnnual ? 'YoY' : 'QoQ'})</span>
      </div>
    </div>
  );
}

// ============================================================
// MARGIN TRENDS CHART
// ============================================================

interface MarginDataPoint {
  fiscal_date: string;
  total_revenue?: number;
  gross_profit?: number;
  operating_income?: number;
  net_income?: number;
}

interface MarginChartProps {
  data: MarginDataPoint[];
  className?: string;
  title?: string;
  ticker?: string;
}

// Margin series configuration
const MARGIN_SERIES = [
  { key: 'gross_margin', name: 'Gross Margin', color: '#10b981' },
  { key: 'operating_margin', name: 'Operating Margin', color: '#f59e0b' },
  { key: 'net_margin', name: 'Net Margin', color: '#a855f7' },
] as const;

type MarginKey = typeof MARGIN_SERIES[number]['key'];

export function MarginChart({ data, className, title = 'Margin Trends', ticker }: MarginChartProps) {
  // Track which series are visible
  const [visibleSeries, setVisibleSeries] = useState<Set<MarginKey>>(
    new Set(MARGIN_SERIES.map(s => s.key))
  );

  // Toggle series visibility
  const handleLegendClick = useCallback((dataKey: string) => {
    setVisibleSeries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dataKey as MarginKey)) {
        // Don't allow hiding all series
        if (newSet.size > 1) {
          newSet.delete(dataKey as MarginKey);
        }
      } else {
        newSet.add(dataKey as MarginKey);
      }
      return newSet;
    });
  }, []);

  // Determine if data is annual (gaps > 200 days between points)
  const isAnnual = useMemo(() => {
    if (!data || data.length < 2) return false;
    const sorted = [...data].sort((a, b) =>
      new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime()
    );
    const gap = new Date(sorted[1].fiscal_date).getTime() - new Date(sorted[0].fiscal_date).getTime();
    return gap > 200 * 24 * 60 * 60 * 1000; // > 200 days = annual
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data]
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime())
      .slice(-8);

    return sorted.map((point, index) => {
      const revenue = point.total_revenue || 1;
      const grossMargin = point.gross_profit ? point.gross_profit / revenue : undefined;
      const operatingMargin = point.operating_income ? point.operating_income / revenue : undefined;
      const netMargin = point.net_income ? point.net_income / revenue : undefined;

      // Calculate period-over-period change in percentage points
      let grossChange: number | undefined;
      let operatingChange: number | undefined;
      let netChange: number | undefined;

      if (index > 0) {
        const prev = sorted[index - 1];
        const prevRevenue = prev.total_revenue || 1;
        const prevGross = prev.gross_profit ? prev.gross_profit / prevRevenue : undefined;
        const prevOperating = prev.operating_income ? prev.operating_income / prevRevenue : undefined;
        const prevNet = prev.net_income ? prev.net_income / prevRevenue : undefined;

        if (grossMargin !== undefined && prevGross !== undefined) {
          grossChange = (grossMargin - prevGross) * 100; // Convert to percentage points
        }
        if (operatingMargin !== undefined && prevOperating !== undefined) {
          operatingChange = (operatingMargin - prevOperating) * 100;
        }
        if (netMargin !== undefined && prevNet !== undefined) {
          netChange = (netMargin - prevNet) * 100;
        }
      }

      return {
        date: isAnnual ? formatYear(point.fiscal_date) : formatQuarter(point.fiscal_date),
        gross_margin: grossMargin,
        operating_margin: operatingMargin,
        net_margin: netMargin,
        gross_change: grossChange,
        operating_change: operatingChange,
        net_change: netChange,
      };
    });
  }, [data, isAnnual]);

  // Calculate dynamic bar size based on visible series
  const visibleCount = visibleSeries.size;
  const barSize = visibleCount === 1 ? 32 : visibleCount === 2 ? 24 : 18;

  if (!data || data.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No margin data available
      </div>
    );
  }

  // Custom legend with clickable items
  const renderLegend = () => (
    <div className="flex justify-center gap-4 mt-2">
      {MARGIN_SERIES.map(series => {
        const isVisible = visibleSeries.has(series.key);
        return (
          <button
            key={series.key}
            onClick={() => handleLegendClick(series.key)}
            className={clsx(
              'flex items-center gap-1.5 text-xs transition-opacity cursor-pointer',
              isVisible ? 'opacity-100' : 'opacity-40'
            )}
          >
            <span
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: series.color }}
            />
            <span className="text-[var(--color-text-muted)]">{series.name}</span>
          </button>
        );
      })}
    </div>
  );

  // Custom tooltip with period-over-period changes
  const MarginTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const dataPoint = payload[0]?.payload;

    return (
      <div className="bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg text-xs">
        <p className="font-medium text-[var(--color-text-primary)] mb-2">{label}</p>
        {MARGIN_SERIES.map(series => {
          if (!visibleSeries.has(series.key)) return null;
          const value = dataPoint[series.key];
          const changeKey = series.key.replace('_margin', '_change');
          const change = dataPoint[changeKey];

          if (value === undefined) return null;

          return (
            <div key={series.key} className="flex items-center justify-between gap-4 py-0.5">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: series.color }} />
                <span className="text-[var(--color-text-muted)]">{series.name}:</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-[var(--color-text-primary)]">
                  {formatPercent(value)}
                </span>
                {change !== undefined && (
                  <span className={clsx(
                    'text-[10px] font-medium',
                    change >= 0 ? 'text-emerald-500' : 'text-red-500'
                  )}>
                    {change >= 0 ? '+' : ''}{change.toFixed(1)}pp
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={title} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
              domain={[-0.2, 1]}
            />
            <Tooltip content={<MarginTooltip />} />
            <ReferenceLine y={0} stroke="var(--color-border)" />

            {visibleSeries.has('gross_margin') && (
              <Bar
                dataKey="gross_margin"
                name="Gross Margin"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
                barSize={barSize}
              />
            )}
            {visibleSeries.has('operating_margin') && (
              <Bar
                dataKey="operating_margin"
                name="Operating Margin"
                fill="#f59e0b"
                radius={[4, 4, 0, 0]}
                barSize={barSize}
              />
            )}
            {visibleSeries.has('net_margin') && (
              <Bar
                dataKey="net_margin"
                name="Net Margin"
                fill="#a855f7"
                radius={[4, 4, 0, 0]}
                barSize={barSize}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {renderLegend()}
      <p className="text-[10px] text-[var(--color-text-muted)] text-center mt-1">
        Click legend to show/hide series • Tooltip shows {isAnnual ? 'YoY' : 'QoQ'} change in pp
      </p>
    </div>
  );
}

// ============================================================
// EPS GROWTH CHART (Annual EPS Trend with YoY Growth)
// ============================================================

interface EPSGrowthDataPoint {
  fiscal_date: string;
  reported_eps?: number;
}

interface QuarterlyEarning {
  fiscal_date: string;
  reported_eps?: number;
}

interface EPSGrowthChartProps {
  data: EPSGrowthDataPoint[];
  quarterlyData?: QuarterlyEarning[];
  forwardEps?: number;
  className?: string;
  title?: string;
  ticker?: string;
}

export function EPSGrowthChart({ data, quarterlyData, forwardEps, className, title = 'EPS Trend', ticker }: EPSGrowthChartProps) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const currentYear = new Date().getFullYear();

    // Count quarters reported for each year from quarterly data
    const quartersPerYear: Record<number, number> = {};
    if (quarterlyData) {
      quarterlyData.forEach(q => {
        const year = new Date(q.fiscal_date).getFullYear();
        quartersPerYear[year] = (quartersPerYear[year] || 0) + 1;
      });
    }

    const sorted = [...data]
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime())
      .slice(-10);

    return sorted.map((point, index) => {
      const year = new Date(point.fiscal_date).getFullYear();
      const prevEps = index > 0 ? sorted[index - 1].reported_eps : undefined;

      // Detect if this is a partial year (current or recent year with < 4 quarters)
      const quartersReported = quartersPerYear[year] || 4;
      const isPartialYear = year >= currentYear - 1 && quartersReported < 4;

      // For partial years, don't show misleading YoY growth
      const epsGrowth = isPartialYear ? undefined : calculateGrowth(point.reported_eps, prevEps);

      // Calculate estimated full year EPS for partial years
      let estimatedFullYear: number | undefined;
      if (isPartialYear && point.reported_eps && quartersReported > 0) {
        // Annualize the partial data (e.g., 3 quarters * 4/3)
        const annualized = (point.reported_eps / quartersReported) * 4;
        // Use forward EPS if available and it's the current year, otherwise use annualized
        if (year === currentYear && forwardEps) {
          estimatedFullYear = forwardEps;
        } else {
          estimatedFullYear = annualized;
        }
      }

      return {
        ...point,
        date: isPartialYear ? `${year}*` : year.toString(),
        year,
        epsGrowth,
        isPartialYear,
        quartersReported,
        estimatedFullYear,
        // For stacked bar: estimate portion is the difference
        estimatedPortion: estimatedFullYear && point.reported_eps
          ? Math.max(0, estimatedFullYear - point.reported_eps)
          : undefined,
      };
    });
  }, [data, quarterlyData, forwardEps]);

  const hasPartialYear = chartData.some(d => d.isPartialYear);

  if (!data || data.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No EPS data available
      </div>
    );
  }

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={title} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={formatEPS}
            />
            <Tooltip
              content={
                <CustomTooltip
                  formatFn={(val, key) => {
                    if (key === 'epsGrowth') return formatPercentChange(val);
                    if (key === 'estimatedPortion') return `+${formatEPS(val)} (Est.)`;
                    return formatEPS(val);
                  }}
                />
              }
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine y={0} stroke="var(--color-border)" strokeDasharray="3 3" />

            <Bar
              dataKey="reported_eps"
              name="Reported EPS"
              stackId="eps"
              fill="#3b82f6"
              radius={[0, 0, 0, 0]}
            >
              <LabelList dataKey="epsGrowth" content={<GrowthLabel />} />
            </Bar>
            <Bar
              dataKey="estimatedPortion"
              name="Estimated (Remaining)"
              stackId="eps"
              fill="#1e40af"
              radius={[4, 4, 0, 0]}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-[var(--color-text-muted)]">
        {hasPartialYear ? (
          <>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500" />
              <span>Reported</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-800" />
              <span>Estimated</span>
            </div>
            <span>* = Partial year (YTD)</span>
          </>
        ) : (
          <span>YoY growth % shown above each bar</span>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SHARE DILUTION CHART - Buybacks vs Stock-Based Compensation
// ============================================================

interface ShareDilutionChartProps {
  data: {
    fiscal_date?: string;
    stock_repurchase?: number;
    stock_based_compensation?: number;
    net_shareholder_return?: number;
  }[];
  className?: string;
  title?: string;
  ticker?: string;
}

export function ShareDilutionChart({ data, className, title = 'Buybacks vs Stock-Based Compensation', ticker }: ShareDilutionChartProps) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Sort by date and take last 8 periods
    // Include rows where stock_repurchase is a number (including 0), or stock_based_compensation exists
    const sorted = [...data]
      .filter(d => d.fiscal_date && (
        typeof d.stock_repurchase === 'number' ||
        typeof d.stock_based_compensation === 'number'
      ))
      .sort((a, b) => new Date(a.fiscal_date!).getTime() - new Date(b.fiscal_date!).getTime())
      .slice(-8);

    return sorted.map((point) => {
      // Buybacks are typically negative (cash outflow), we want to show as positive
      const buybacks = point.stock_repurchase ? Math.abs(point.stock_repurchase) : 0;
      const sbc = point.stock_based_compensation || 0;
      // Net = Buybacks - SBC (positive = returning value to shareholders)
      // Only show net if we have SBC data, otherwise just show buybacks
      const netReturn = typeof point.stock_based_compensation === 'number' ? buybacks - sbc : null;

      return {
        date: formatYear(point.fiscal_date!),
        buybacks,
        sbc: typeof point.stock_based_compensation === 'number' ? sbc : null,
        netReturn,
      };
    });
  }, [data]);

  // Check if we have SBC data
  const hasSbcData = chartData.some(d => d.sbc !== null);

  if (!data || data.length === 0 || chartData.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No buyback data available
      </div>
    );
  }

  // Dynamic title based on available data
  const displayTitle = hasSbcData ? title : 'Stock Buybacks';

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={displayTitle} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={formatLargeNumber}
            />
            <Tooltip
              content={
                <CustomTooltip
                  formatFn={(val) => formatLargeNumber(val)}
                />
              }
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine y={0} stroke="var(--color-border)" strokeDasharray="3 3" />

            <Bar dataKey="buybacks" name="Buybacks" fill="#10b981" radius={[4, 4, 0, 0]} />
            {hasSbcData && (
              <>
                <Bar dataKey="sbc" name="Stock-Based Comp" fill="#ef4444" radius={[4, 4, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="netReturn"
                  name="Net Shareholder Return"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ fill: '#f59e0b', strokeWidth: 2, r: 4 }}
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-[var(--color-text-muted)] text-center mt-2">
        {hasSbcData ? (
          <>Net Return = Buybacks − SBC | Positive = Returning value, Negative = Diluting shareholders</>
        ) : (
          <>Stock buybacks (cash returned to shareholders) | SBC data not available from API</>
        )}
      </p>
    </div>
  );
}

// ============================================================
// SHARES OUTSTANDING CHART - Track dilution over time
// ============================================================

interface SharesOutstandingChartProps {
  data: {
    fiscal_date?: string;
    common_stock_shares_outstanding?: number;
  }[];
  // Optional: pass SBC data to show if dilution includes/excludes unvested awards
  sbcData?: {
    fiscal_date?: string;
    stock_based_compensation?: number;
  }[];
  className?: string;
  title?: string;
  ticker?: string;
}

export function SharesOutstandingChart({ data, sbcData, className, title = 'Shares Outstanding', ticker }: SharesOutstandingChartProps) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Sort by date and filter valid entries
    const sorted = [...data]
      .filter(d => d.fiscal_date && d.common_stock_shares_outstanding)
      .sort((a, b) => new Date(a.fiscal_date!).getTime() - new Date(b.fiscal_date!).getTime())
      .slice(-8);

    return sorted.map((point, index) => {
      const prev = index > 0 ? sorted[index - 1] : null;
      const shares = point.common_stock_shares_outstanding!;
      const prevShares = prev?.common_stock_shares_outstanding;

      // Calculate YoY change
      let yoyChange: number | undefined;
      if (prevShares) {
        yoyChange = ((shares - prevShares) / prevShares) * 100;
      }

      return {
        date: formatYear(point.fiscal_date!),
        shares,
        sharesInBillions: shares / 1e9,
        yoyChange,
      };
    });
  }, [data]);

  // Check if we have SBC data to determine what to show in the note
  const hasSbcData = sbcData && sbcData.some(d => typeof d.stock_based_compensation === 'number' && d.stock_based_compensation > 0);

  if (!data || data.length === 0 || chartData.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No shares outstanding data available
      </div>
    );
  }

  // Determine if share count is growing or shrinking overall
  const firstShares = chartData[0]?.shares || 0;
  const lastShares = chartData[chartData.length - 1]?.shares || 0;
  const overallChange = firstShares > 0 ? ((lastShares - firstShares) / firstShares) * 100 : 0;
  const isDiluting = overallChange > 0;

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={title} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="date"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={(val) => `${(val).toFixed(1)}B`}
              domain={[0, 'auto']}
            />
            <Tooltip
              content={
                <CustomTooltip
                  formatFn={(val, key) => {
                    if (key === 'yoyChange') return `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`;
                    if (key === 'sharesInBillions') return `${val.toFixed(2)}B shares`;
                    return formatLargeNumber(val);
                  }}
                />
              }
            />
            <Bar
              dataKey="sharesInBillions"
              name="Shares Outstanding (B)"
              fill={isDiluting ? '#ef4444' : '#10b981'}
              radius={[4, 4, 0, 0]}
            >
              <LabelList
                dataKey="yoyChange"
                content={(props: any) => {
                  const { x = 0, y = 0, width = 0, value } = props;
                  if (value === undefined || value === null) return null;
                  const color = value <= 0 ? '#10b981' : '#ef4444';
                  return (
                    <text x={Number(x) + Number(width) / 2} y={Number(y) - 5} fill={color} textAnchor="middle" fontSize={10} fontWeight="bold">
                      {value >= 0 ? '+' : ''}{Number(value).toFixed(1)}%
                    </text>
                  );
                }}
              />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* Summary line */}
      <p className="text-xs text-[var(--color-text-muted)] text-center mt-2">
        {isDiluting ? (
          <span className="text-red-500">↑ Dilution: Share count increased {overallChange.toFixed(1)}% over this period</span>
        ) : (
          <span className="text-emerald-500">↓ Buybacks: Share count decreased {Math.abs(overallChange).toFixed(1)}% over this period</span>
        )}
      </p>
      {/* Clarification about what shares outstanding includes */}
      <p className="text-xs text-[var(--color-text-muted)] text-center mt-1">
        {hasSbcData ? (
          <span>
            <span className="font-medium">Note:</span> Shows issued shares only. Does not include unvested SBC awards.
            See Buybacks vs SBC chart for compensation impact.
          </span>
        ) : (
          <span>
            <span className="font-medium">Note:</span> Shows issued shares from balance sheet.
            Excludes unvested stock options & RSUs (future dilution not reflected).
          </span>
        )}
      </p>
    </div>
  );
}

// ============================================================
// FCF PER SHARE CHART - Free Cash Flow per Share over time
// ============================================================

interface FCFPerShareChartProps {
  cashFlowData: {
    fiscal_date?: string;
    free_cash_flow?: number;
  }[];
  balanceSheetData: {
    fiscal_date?: string;
    common_stock_shares_outstanding?: number;
  }[];
  className?: string;
  title?: string;
  ticker?: string;
}

export function FCFPerShareChart({
  cashFlowData,
  balanceSheetData,
  className,
  title = 'Free Cash Flow Per Share',
  ticker,
}: FCFPerShareChartProps) {
  const chartData = useMemo(() => {
    if (!cashFlowData || !balanceSheetData || cashFlowData.length === 0 || balanceSheetData.length === 0) {
      return [];
    }

    // Create a map of balance sheet data by year for matching
    const sharesMap = new Map<string, number>();
    balanceSheetData.forEach(item => {
      if (item.fiscal_date && item.common_stock_shares_outstanding) {
        const year = formatYear(item.fiscal_date);
        sharesMap.set(year, item.common_stock_shares_outstanding);
      }
    });

    // Combine cash flow with shares outstanding
    const combined = cashFlowData
      .filter(cf => cf.fiscal_date && cf.free_cash_flow !== undefined && cf.free_cash_flow !== null)
      .map(cf => {
        const year = formatYear(cf.fiscal_date!);
        const shares = sharesMap.get(year);
        if (!shares) return null;

        const fcfPerShare = cf.free_cash_flow! / shares;
        return {
          fiscal_date: cf.fiscal_date!,
          year,
          fcf: cf.free_cash_flow!,
          shares,
          fcfPerShare,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => new Date(a.fiscal_date).getTime() - new Date(b.fiscal_date).getTime())
      .slice(-8);

    // Calculate YoY growth
    return combined.map((point, index) => {
      const prev = index > 0 ? combined[index - 1] : null;
      let yoyGrowth: number | undefined;
      if (prev && prev.fcfPerShare !== 0) {
        yoyGrowth = ((point.fcfPerShare - prev.fcfPerShare) / Math.abs(prev.fcfPerShare)) * 100;
      }

      return {
        ...point,
        yoyGrowth,
      };
    });
  }, [cashFlowData, balanceSheetData]);

  if (chartData.length === 0) {
    return (
      <div className={clsx(
        'flex items-center justify-center h-64 rounded-lg',
        'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]',
        className
      )}>
        No FCF per share data available
      </div>
    );
  }

  // Calculate overall trend
  const firstFCF = chartData[0]?.fcfPerShare || 0;
  const lastFCF = chartData[chartData.length - 1]?.fcfPerShare || 0;
  const overallGrowth = firstFCF !== 0 ? ((lastFCF - firstFCF) / Math.abs(firstFCF)) * 100 : 0;
  const isGrowing = lastFCF > firstFCF;

  // Calculate CAGR if we have multiple years
  const years = chartData.length - 1;
  const cagr = years > 0 && firstFCF > 0 && lastFCF > 0
    ? (Math.pow(lastFCF / firstFCF, 1 / years) - 1) * 100
    : null;

  return (
    <div className={clsx('w-full', className)}>
      <ChartTitle ticker={ticker} title={title} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minHeight={1}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
            <XAxis
              dataKey="year"
              tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              tickFormatter={(val) => `$${val.toFixed(2)}`}
            />
            <Tooltip
              content={
                <CustomTooltip
                  formatFn={(val, key) => {
                    if (key === 'yoyGrowth') return `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`;
                    if (key === 'fcfPerShare') return `$${val.toFixed(2)}`;
                    if (key === 'fcf') return formatLargeNumber(val);
                    if (key === 'shares') return `${(val / 1e9).toFixed(2)}B shares`;
                    return String(val);
                  }}
                />
              }
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine y={0} stroke="var(--color-border)" strokeDasharray="3 3" />

            <Bar
              dataKey="fcfPerShare"
              name="FCF/Share"
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.fcfPerShare >= 0 ? '#10b981' : '#ef4444'}
                />
              ))}
              <LabelList
                dataKey="yoyGrowth"
                content={(props: any) => {
                  const { x = 0, y = 0, width = 0, value } = props;
                  if (value === undefined || value === null) return null;
                  const color = value >= 0 ? '#10b981' : '#ef4444';
                  return (
                    <text x={Number(x) + Number(width) / 2} y={Number(y) - 5} fill={color} textAnchor="middle" fontSize={10} fontWeight="bold">
                      {value >= 0 ? '+' : ''}{Number(value).toFixed(0)}%
                    </text>
                  );
                }}
              />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* Summary metrics */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-2 text-xs">
        <span className={clsx(
          'font-medium',
          isGrowing ? 'text-emerald-500' : 'text-red-500'
        )}>
          {isGrowing ? '↑' : '↓'} {Math.abs(overallGrowth).toFixed(1)}% total change
        </span>
        {cagr !== null && (
          <span className={clsx(
            'font-medium',
            cagr >= 0 ? 'text-emerald-500' : 'text-red-500'
          )}>
            {cagr >= 0 ? '+' : ''}{cagr.toFixed(1)}% CAGR
          </span>
        )}
        <span className="text-[var(--color-text-muted)]">
          Latest: ${lastFCF.toFixed(2)}/share
        </span>
      </div>
      <p className="text-xs text-[var(--color-text-muted)] text-center mt-1">
        FCF/Share = Free Cash Flow ÷ Shares Outstanding | Higher is better for shareholders
      </p>
    </div>
  );
}
